import { ArrowLeft } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import BottomNavBar from "../../components/BottomNavBar";
import welcomeBg from "../../assets/homebg.png";
import mealApiService from "../../services/MealApiService";

interface HistoryDayData {
  date: string;
  displayDate: string;
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  meals: number;
}

const MealHistory = () => {
  const navigate = useNavigate();
  const [selectedDate] = useState(new Date());
  const [historyData, setHistoryData] = useState<HistoryDayData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch meal history data
  useEffect(() => {
    const fetchMealHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const currentDate = new Date();
        const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
        const year = currentDate.getFullYear();

        const response = await mealApiService.getMealHistory(month, year);

        // Transform API response to display format
        const transformedData: HistoryDayData[] = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        response.results.forEach(dayData => {
          // Parse date and create display text
          const mealDate = new Date(dayData.date);
          mealDate.setHours(0, 0, 0, 0);

          let displayDate = dayData.date;
          const diffTime = today.getTime() - mealDate.getTime();
          const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

          if (diffDays === 0) {
            displayDate = "Today";
          } else if (diffDays === 1) {
            displayDate = "Yesterday";
          } else if (diffDays > 1) {
            displayDate = `${diffDays} days ago`;
          } else {
            displayDate = mealDate.toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            });
          }

          transformedData.push({
            date: dayData.date,
            displayDate,
            calories: Math.round(dayData.kcal),
            protein: Math.round(dayData.protein),
            carbs: Math.round(dayData.carbs),
            fat: Math.round(dayData.fat),
            meals: dayData.total_meals,
          });
        });

        // Sort by date descending (most recent first)
        transformedData.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );

        setHistoryData(transformedData);
      } catch (err: any) {
        setError(err.message || "Failed to load meal history");
      } finally {
        setLoading(false);
      }
    };

    fetchMealHistory();
  }, []);

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto pb-16"
        style={{ height: "calc(100vh - 80px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 pb-4 pt-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="h-10 w-10 rounded-xl bg-card/90 shadow-sm"
          >
            <ArrowLeft className="h-5 w-5 text-foreground" />
          </Button>
          <div className="flex-1">
            <h1 className="text-title2 font-bold text-foreground">Meal History</h1>
            <p className="text-caption1 text-muted-foreground">
              {selectedDate.toLocaleDateString("en-US", {
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>
          {/* Calendar button temporarily hidden */}
          {/* <Button
            variant="outline"
            size="icon"
            className="h-10 w-10 rounded-xl border-border bg-card/90 shadow-sm"
          >
            <CalendarIcon className="h-5 w-5 text-orange-500" />
          </Button> */}
        </div>

        <div className="space-y-4 px-4">
          {loading ? (
            <Card className="rounded-2xl border-2 border-primary/20 bg-card/90 p-5 shadow-md">
              <p className="text-center text-muted-foreground">
                Loading meal history...
              </p>
            </Card>
          ) : error ? (
            <Card className="rounded-2xl border-2 border-primary/20 bg-card/90 p-5 shadow-md">
              <p className="text-center text-red-600">{error}</p>
            </Card>
          ) : historyData.length === 0 ? (
            <Card className="rounded-2xl border-2 border-primary/20 bg-card/90 p-5 shadow-md">
              <p className="text-center text-muted-foreground">
                No meal history found
              </p>
            </Card>
          ) : (
            historyData.map((day, index) => (
              <Card
                key={index}
                className="rounded-2xl border-2 border-primary/20 bg-card/90 p-5 shadow-md"
              >
                <div className="mb-4 flex items-center justify-between">
                  <div>
                    <h3 className="text-title3 font-bold text-foreground">
                      {day.displayDate}
                    </h3>
                    <p className="text-subhead font-semibold text-muted-foreground">
                      {day.meals} meals logged
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-large-title font-bold text-blue-600">
                      {day.calories}
                    </div>
                    <p className="text-subhead font-bold text-muted-foreground">
                      kcal
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border-2 border-blue-200 bg-blue-50 py-3 text-center dark:border-blue-800 dark:bg-blue-950">
                    <div className="text-callout font-bold text-blue-600 dark:text-blue-400">
                      {day.protein}g
                    </div>
                    <div className="text-caption1 font-bold text-muted-foreground">
                      Protein
                    </div>
                  </div>
                  <div className="rounded-lg border-2 border-green-200 bg-green-50 py-3 text-center dark:border-green-800 dark:bg-green-950">
                    <div className="text-callout font-bold text-green-600 dark:text-green-400">
                      {day.carbs}g
                    </div>
                    <div className="text-caption1 font-bold text-muted-foreground">
                      Carbs
                    </div>
                  </div>
                  <div className="rounded-lg border-2 border-orange-200 bg-orange-50 py-3 text-center dark:border-orange-800 dark:bg-orange-950">
                    <div className="text-callout font-bold text-orange-600 dark:text-orange-400">
                      {day.fat}g
                    </div>
                    <div className="text-caption1 font-bold text-muted-foreground">
                      Fat
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      <BottomNavBar activeIndex={1} />
    </div>
  );
};

export default MealHistory;
